import { memo } from 'react';
import { format } from 'date-fns';
import {
  getDeadlineDaysLeft,
  getDayWordRu,
  getRemainingHeadlineRu,
  getDeadlineUrgencyTier,
} from '@/utils/projectDeadlineRemaining';
import { highlightPlainText } from '@/utils/highlightSearchTerms';

export interface ProjectDateCellContentProps {
  /** Дата в формате YYYY-MM-DD (как с API) */
  dateStr: string | null | undefined;
  /** Проект в статусе «Готов» — показываем дату без срочности */
  isCompleted: boolean;
  /** Текст для «сегодня», например «Дедлайн сегодня», «Выход сегодня», «Проверка сегодня» */
  todayLabel: string;
  /** Дополнительный класс контейнера (например projects-table__deadline-cell или projects-table__paper-plan-cell) */
  containerClassName?: string;
  /** Слова глобального поиска — подсветка в отформатированной дате */
  searchHighlightWords?: string[];
}

/**
 * Единый компонент отображения даты в формате dd.MM.yyyy с пиллом и подписью «осталось/просрочен».
 * Используется в ячейках Дедлайн, План бумаги, Дедлайн проверки.
 */
export const ProjectDateCellContent = memo(function ProjectDateCellContent({
  dateStr,
  isCompleted,
  todayLabel,
  containerClassName = 'projects-table__deadline-cell',
  searchHighlightWords,
}: ProjectDateCellContentProps) {
  if (!dateStr?.trim()) return null;

  const d = new Date(dateStr);
  if (isNaN(d.getTime()))
    return (
      <span>
        {searchHighlightWords?.length
          ? highlightPlainText(dateStr, searchHighlightWords)
          : dateStr}
      </span>
    );

  const formatted = format(d, 'dd.MM.yyyy');
  const formattedNode =
    searchHighlightWords?.length && searchHighlightWords.length > 0
      ? highlightPlainText(formatted, searchHighlightWords)
      : formatted;
  const daysLeft = getDeadlineDaysLeft(dateStr);
  const isOverdue = daysLeft !== null && daysLeft < 0;
  const isToday = daysLeft === 0;
  const tier = getDeadlineUrgencyTier(daysLeft, isCompleted);

  const tierToPill: Record<string, string> = {
    complete: 'projects-table__deadline-pill_complete',
    urgent: 'projects-table__deadline-pill_urgent',
    warning: 'projects-table__deadline-pill_warning',
    ok: 'projects-table__deadline-pill_ok',
    neutral: 'projects-table__deadline-pill_neutral',
    default: 'projects-table__deadline-pill',
  };
  const pillClass = tierToPill[tier] ?? 'projects-table__deadline-pill';

  const textClass =
    isCompleted
      ? 'projects-table__deadline-text_complete'
      : isOverdue || (daysLeft !== null && daysLeft <= 7)
        ? 'projects-table__deadline-text_urgent'
        : '';

  return (
    <div className={containerClassName}>
      <span className={`projects-table__deadline-pill ${pillClass}`}>
        <span className={textClass}>{formattedNode}</span>
      </span>
      {!isCompleted && daysLeft !== null && !isNaN(daysLeft) && (
        <span className="projects-table__deadline-remaining">
          {isOverdue ? (
            <>Просрочен на <strong>{Math.abs(daysLeft)}</strong> {getDayWordRu(daysLeft)}</>
          ) : isToday ? (
            <strong className="projects-table__deadline-today">{todayLabel}</strong>
          ) : (
            <>{getRemainingHeadlineRu(daysLeft)} <strong>{daysLeft}</strong> {getDayWordRu(daysLeft)}</>
          )}
        </span>
      )}
    </div>
  );
});
